import { useEffect, useState } from 'react';

/**
 * useUnsavedChanges — reusable leave-guard hook.
 *
 * Extracted from the FormBuilder pattern (FormBuilder.jsx:464,570-574,610-613).
 * Provides:
 *   - isDirty / setIsDirty        — track whether there are unsaved changes
 *   - blockedNavigate(path)       — wraps navigate(); if dirty, opens a confirm
 *                                   modal instead of navigating immediately
 *   - showNavConfirm / pendingNav  — state for rendering the confirm modal
 *   - confirmLeave()              — "Leave" button: clears dirty + navigates
 *   - cancelLeave()               — "Stay" button: closes modal, clears pending
 *
 * The beforeunload handler (tab close / refresh) is registered automatically
 * while isDirty is true.
 *
 * @param {Function} navigate — react-router's useNavigate() result
 */
export function useUnsavedChanges(navigate) {
    const [isDirty, setIsDirty] = useState(false);
    const [showNavConfirm, setShowNavConfirm] = useState(false);
    const [pendingNav, setPendingNav] = useState(null);

    // beforeunload guard — fires on tab close / refresh while dirty
    useEffect(() => {
        const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // In-app navigation guard — blocks route changes while dirty
    const blockedNavigate = (path) => {
        if (isDirty) { setPendingNav(path); setShowNavConfirm(true); }
        else navigate(path);
    };

    // Confirm modal actions
    const confirmLeave = () => {
        setShowNavConfirm(false);
        setIsDirty(false);
        if (pendingNav) navigate(pendingNav);
        setPendingNav(null);
    };

    const cancelLeave = () => {
        setShowNavConfirm(false);
        setPendingNav(null);
    };

    return {
        isDirty,
        setIsDirty,
        blockedNavigate,
        showNavConfirm,
        pendingNav,
        confirmLeave,
        cancelLeave,
    };
}
